import { useEffect, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import {
  getAlly, createAlly, updateAlly,
  getAllyProducts, deleteAllyProduct,
} from '../../services/adminService';
import { buildSlug } from '../../utils/slug';
import { formatPrice } from '../../utils/format';
import ImageUploadField from '../../components/ImageUploadField';

const ALLY_IMAGE_BUCKET = 'products_images';

const inputStyle = {
  width: '100%',
  padding: '0.5rem 0.75rem',
  border: '1px solid #ddd',
  borderRadius: '8px',
  fontSize: '0.9rem',
  outline: 'none',
  boxSizing: 'border-box',
};

const labelStyle = {
  display: 'block',
  fontSize: '0.85rem',
  fontWeight: '600',
  color: '#555',
  marginBottom: '0.35rem',
};

const AdminAllyForm = () => {
  const { id } = useParams();
  const isNew = !id || id === 'new';
  const { session } = useAuth();
  const navigate = useNavigate();

  const [form, setForm] = useState({
    name: '', slug: '', description: '', catalog_title: '', catalog_description: '',
    profile_image_url: '', instagram_handle: '', website_url: '', is_active: true,
    contact_email: '', bank_name: '', bank_beneficiary: '', bank_clabe: '',
  });
  const [slugManuallyEdited, setSlugManuallyEdited] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [loadingAlly, setLoadingAlly] = useState(!isNew);
  const [formError, setFormError] = useState(null);
  const [formSuccess, setFormSuccess] = useState(null);

  // Products state (edit mode only)
  const [products, setProducts] = useState([]);
  const [loadingProducts, setLoadingProducts] = useState(false);
  const [deletingProductId, setDeletingProductId] = useState(null);

  // Load existing ally
  useEffect(() => {
    if (isNew || !session?.access_token) return;
    let cancelled = false;
    setLoadingAlly(true);

    getAlly(session.access_token, id)
      .then((data) => {
        if (cancelled) return;
        const a = data.ally;
        setForm({
          name: a.name || '',
          slug: a.slug || '',
          description: a.description || '',
          catalog_title: a.catalog_title || '',
          catalog_description: a.catalog_description || '',
          profile_image_url: a.profile_image_url || '',
          instagram_handle: a.instagram_handle || '',
          website_url: a.website_url || '',
          is_active: a.is_active !== false,
          contact_email: a.contact_email || '',
          bank_name: a.bank_name || '',
          bank_beneficiary: a.bank_beneficiary || '',
          bank_clabe: a.bank_clabe || '',
        });
        setSlugManuallyEdited(true);
        setLoadingAlly(false);
      })
      .catch((err) => { if (!cancelled) { setFormError(err.message); setLoadingAlly(false); } });

    return () => { cancelled = true; };
  }, [id, isNew, session?.access_token]);

  // Load products in edit mode
  useEffect(() => {
    if (isNew || !session?.access_token) return;
    let cancelled = false;
    setLoadingProducts(true);

    getAllyProducts(session.access_token, id)
      .then((data) => { if (!cancelled) { setProducts(data.products || []); setLoadingProducts(false); } })
      .catch(() => { if (!cancelled) setLoadingProducts(false); });

    return () => { cancelled = true; };
  }, [id, isNew, session?.access_token]);

  const handleNameChange = (e) => {
    const name = e.target.value;
    setForm((f) => ({
      ...f,
      name,
      slug: slugManuallyEdited ? f.slug : buildSlug(name),
    }));
  };

  const handleSlugChange = (e) => {
    setSlugManuallyEdited(true);
    setForm((f) => ({ ...f, slug: e.target.value }));
  };

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setForm((f) => ({ ...f, [name]: type === 'checkbox' ? checked : value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setFormError(null);
    setFormSuccess(null);
    setSaving(true);
    try {
      const payload = {
        name: form.name.trim(),
        slug: form.slug.trim(),
        description: form.description.trim() || null,
        catalog_title: form.catalog_title.trim() || null,
        catalog_description: form.catalog_description.trim() || null,
        profile_image_url: form.profile_image_url.trim() || null,
        instagram_handle: form.instagram_handle.trim() || null,
        website_url: form.website_url.trim() || null,
        is_active: form.is_active,
        contact_email: form.contact_email.trim() || null,
        bank_name: form.bank_name.trim() || null,
        bank_beneficiary: form.bank_beneficiary.trim() || null,
        bank_clabe: form.bank_clabe.trim() || null,
      };

      if (isNew) {
        const data = await createAlly(session.access_token, payload);
        navigate(`/admin/allies/${data.ally.id}`, { replace: true });
      } else {
        await updateAlly(session.access_token, id, payload);
        setFormSuccess('Aliado guardado.');
      }
    } catch (err) {
      setFormError(err.message);
    } finally {
      setSaving(false);
    }
  };

  const handleDeleteProduct = async (product) => {
    if (!window.confirm(`¿Eliminar "${product.name}"?`)) return;
    setDeletingProductId(product.id);
    try {
      await deleteAllyProduct(session.access_token, product.id);
      setProducts((prev) => prev.filter((p) => p.id !== product.id));
    } catch (err) {
      alert(`Error: ${err.message}`);
    } finally {
      setDeletingProductId(null);
    }
  };

  if (loadingAlly) {
    return (
      <div className="container" style={{ padding: '2rem 0' }}>
        <p style={{ color: '#888' }}>Cargando...</p>
      </div>
    );
  }

  const isBusy = saving || uploading;

  return (
    <div className="container" style={{ padding: '2rem 0 4rem' }}>
      {/* Breadcrumb */}
      <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem', marginBottom: '1.5rem', flexWrap: 'wrap' }}>
        <Link to="/admin" style={{ color: '#888', textDecoration: 'none', fontSize: '0.9rem' }}>← Panel</Link>
        <span style={{ color: '#ccc' }}>/</span>
        <Link to="/admin/allies" style={{ color: '#888', textDecoration: 'none', fontSize: '0.9rem' }}>Aliados</Link>
        <span style={{ color: '#ccc' }}>/</span>
        <span style={{ fontSize: '0.9rem', color: '#333' }}>{isNew ? 'Nuevo' : form.name || id}</span>
      </div>

      <h1 style={{ fontFamily: "'Gambado Sans', sans-serif", fontSize: '1.6rem', color: '#333', marginBottom: '2rem' }}>
        {isNew ? 'Nuevo aliado' : 'Editar aliado'}
      </h1>

      {/* Ally form */}
      <form onSubmit={handleSubmit} style={{ maxWidth: 600 }}>
        <div style={{ display: 'flex', flexDirection: 'column', gap: '1.25rem' }}>

          <div>
            <label style={labelStyle}>Nombre *</label>
            <input name="name" value={form.name} onChange={handleNameChange} required style={inputStyle} />
          </div>

          <div>
            <label style={labelStyle}>Slug *</label>
            <input name="slug" value={form.slug} onChange={handleSlugChange} required style={inputStyle} placeholder="mi-aliado" />
            <p style={{ fontSize: '0.78rem', color: '#aaa', marginTop: '0.25rem' }}>
              URL pública: /ally/{form.slug || '…'}
            </p>
          </div>

          <div>
            <label style={labelStyle}>Descripción</label>
            <textarea name="description" value={form.description} onChange={handleChange}
              rows={4} style={{ ...inputStyle, resize: 'vertical' }} />
          </div>

          <div>
            <label style={labelStyle}>Título del catálogo</label>
            <input name="catalog_title" value={form.catalog_title} onChange={handleChange}
              style={inputStyle} placeholder="Ej. Nuestra colección" />
            <p style={{ fontSize: '0.78rem', color: '#aaa', marginTop: '0.25rem' }}>
              Encabezado que aparece sobre el catálogo de productos.
            </p>
          </div>

          <div>
            <label style={labelStyle}>Descripción del catálogo</label>
            <textarea name="catalog_description" value={form.catalog_description} onChange={handleChange}
              rows={3} style={{ ...inputStyle, resize: 'vertical', maxWidth: '100%' }}
              placeholder="Texto introductorio que aparece encima del catálogo de productos." />
            <p style={{ fontSize: '0.78rem', color: '#aaa', marginTop: '0.25rem' }}>
              Visible en la página pública del aliado, sobre la grilla de productos.
            </p>
          </div>

          <ImageUploadField
            label="Imagen de perfil"
            value={form.profile_image_url}
            onChange={(url) => setForm((f) => ({ ...f, profile_image_url: url }))}
            bucket={ALLY_IMAGE_BUCKET}
            onUploadingChange={setUploading}
          />

          <div>
            <label style={labelStyle}>Instagram (sin @)</label>
            <input name="instagram_handle" value={form.instagram_handle} onChange={handleChange} style={inputStyle} placeholder="handle" />
          </div>

          <div>
            <label style={labelStyle}>Sitio web</label>
            <input name="website_url" value={form.website_url} onChange={handleChange} style={inputStyle} placeholder="https://…" />
          </div>

          <div>
            <label style={labelStyle}>Email de contacto — copia en correos de venta</label>
            <input
              name="contact_email"
              type="email"
              value={form.contact_email}
              onChange={handleChange}
              style={inputStyle}
              placeholder="aliado@email.com"
            />
            <p style={{ fontSize: '0.78rem', color: '#aaa', marginTop: '0.25rem' }}>
              Cuando se compre un producto de este aliado se enviará una notificación a este correo.
            </p>
          </div>

          <fieldset style={{ border: '1px solid #eee', borderRadius: '8px', padding: '1rem', margin: 0, display: 'flex', flexDirection: 'column', gap: '1rem' }}>
            <legend style={{ ...labelStyle, padding: '0 0.35rem', marginBottom: 0 }}>Cuenta bancaria</legend>
            <p style={{ fontSize: '0.78rem', color: '#aaa', margin: 0 }}>
              No aparece en la página pública del aliado. Si agregas la CLABE, en el checkout se ofrece "Transferencia
              bancaria" y el cliente recibe estos datos (en pantalla y por correo) al confirmar su pedido, para
              transferir directo al aliado.
            </p>
            <div>
              <label htmlFor="bank_name" style={labelStyle}>Banco</label>
              <input id="bank_name" name="bank_name" value={form.bank_name} onChange={handleChange} style={inputStyle} placeholder="BBVA, Banorte…" />
            </div>
            <div>
              <label htmlFor="bank_beneficiary" style={labelStyle}>Beneficiario</label>
              <input id="bank_beneficiary" name="bank_beneficiary" value={form.bank_beneficiary} onChange={handleChange} style={inputStyle} placeholder="Nombre del titular de la cuenta" />
            </div>
            <div>
              <label htmlFor="bank_clabe" style={labelStyle}>CLABE</label>
              <input id="bank_clabe" name="bank_clabe" value={form.bank_clabe} onChange={handleChange}
                inputMode="numeric" pattern="\d{18}" maxLength={18} title="18 dígitos"
                style={{ ...inputStyle, fontFamily: 'monospace' }} placeholder="18 dígitos" />
            </div>
          </fieldset>

          <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
            <input id="is_active" name="is_active" type="checkbox" checked={form.is_active} onChange={handleChange} />
            <label htmlFor="is_active" style={{ fontSize: '0.9rem', color: '#555', cursor: 'pointer' }}>Activo (visible en el sitio)</label>
          </div>

          {formError && <p style={{ color: '#dc3545', fontSize: '0.9rem', margin: 0 }}>{formError}</p>}
          {formSuccess && <p style={{ color: '#28a745', fontSize: '0.9rem', margin: 0 }}>{formSuccess}</p>}

          <div style={{ display: 'flex', gap: '1rem', marginTop: '0.5rem' }}>
            <button type="submit" disabled={isBusy}
              style={{
                padding: '0.6rem 1.5rem',
                background: isBusy ? '#ccc' : '#f59e0b',
                color: 'white',
                border: 'none', borderRadius: '8px', fontWeight: '600', fontSize: '0.9rem',
                cursor: isBusy ? 'default' : 'pointer',
              }}>
              {uploading ? 'Subiendo imagen...' : saving ? 'Guardando...' : isNew ? 'Crear aliado' : 'Guardar cambios'}
            </button>
            <Link to="/admin/allies"
              style={{ padding: '0.6rem 1rem', color: '#888', textDecoration: 'none', fontSize: '0.9rem', display: 'inline-flex', alignItems: 'center' }}>
              Cancelar
            </Link>
          </div>
        </div>
      </form>

      {/* Products section — only in edit mode */}
      {!isNew && (
        <div style={{ marginTop: '3rem' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '1rem', marginBottom: '1rem' }}>
            <h2 style={{ fontFamily: "'Gambado Sans', sans-serif", fontSize: '1.3rem', color: '#333', margin: 0 }}>
              Productos del catálogo
            </h2>
            <Link to={`/admin/allies/${id}/products/new`}
              style={{
                marginLeft: 'auto', padding: '0.45rem 1rem', background: '#f59e0b', color: 'white',
                border: 'none', borderRadius: '8px', fontWeight: '600', fontSize: '0.85rem',
                textDecoration: 'none', whiteSpace: 'nowrap',
              }}>
              + Agregar producto
            </Link>
          </div>

          {loadingProducts ? (
            <p style={{ color: '#888', fontSize: '0.9rem' }}>Cargando productos...</p>
          ) : products.length === 0 ? (
            <p style={{ color: '#aaa', fontSize: '0.9rem' }}>Sin productos. Agrega el primero.</p>
          ) : (
            <div style={{ background: 'white', borderRadius: '12px', boxShadow: '0 2px 8px rgba(0,0,0,0.08)', overflow: 'hidden' }}>
              <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.9rem' }}>
                <thead>
                  <tr style={{ background: '#fafafa' }}>
                    {['Imagen', 'Nombre', 'Artista', 'Precio', 'Orden', 'Activo', ''].map((h) => (
                      <th key={h} style={{ padding: '0.65rem 1rem', textAlign: 'left', color: '#666', fontWeight: '600', borderBottom: '1px solid #f0f0f0', whiteSpace: 'nowrap' }}>{h}</th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {products.map((p) => (
                    <tr key={p.id} style={{ borderBottom: '1px solid #f8f8f8' }}>
                      <td style={{ padding: '0.5rem 1rem' }}>
                        {p.image ? (
                          <img src={p.image} alt={p.name}
                            style={{ width: 40, height: 40, objectFit: 'cover', borderRadius: '6px', border: '1px solid #eee' }}
                            onError={(e) => { e.target.style.display = 'none'; }}
                          />
                        ) : (
                          <div style={{ width: 40, height: 40, borderRadius: '6px', background: '#f0f0f0' }} />
                        )}
                      </td>
                      <td style={{ padding: '0.65rem 1rem', fontWeight: '500', color: '#333' }}>{p.name}</td>
                      <td style={{ padding: '0.65rem 1rem', color: '#888' }}>{p.artist || '—'}</td>
                      <td style={{ padding: '0.65rem 1rem', color: '#333' }}>{formatPrice(p.price, 'MXN')}</td>
                      <td style={{ padding: '0.65rem 1rem', color: '#555', textAlign: 'center' }}>{p.display_order ?? '—'}</td>
                      <td style={{ padding: '0.65rem 1rem', color: p.is_active ? '#28a745' : '#aaa', fontWeight: '600', fontSize: '0.82rem' }}>
                        {p.is_active ? 'Sí' : 'No'}
                        {p.is_sold_out && <span style={{ color: '#dc3545', marginLeft: '0.4rem' }}>· Agotado</span>}
                      </td>
                      <td style={{ padding: '0.65rem 1rem' }}>
                        <div style={{ display: 'flex', gap: '0.75rem' }}>
                          <Link to={`/admin/allies/${id}/products/${p.id}`}
                            style={{ color: '#f59e0b', textDecoration: 'none', fontWeight: '500', fontSize: '0.85rem', whiteSpace: 'nowrap' }}>
                            Editar →
                          </Link>
                          <button onClick={() => handleDeleteProduct(p)} disabled={deletingProductId === p.id}
                            style={{ background: 'none', border: 'none', color: deletingProductId === p.id ? '#ccc' : '#dc3545', cursor: deletingProductId === p.id ? 'default' : 'pointer', fontSize: '0.85rem', fontWeight: '500', padding: 0 }}>
                            {deletingProductId === p.id ? '...' : 'Eliminar'}
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default AdminAllyForm;
